import React, { ReactElement } from 'react'
import { Flex, Text } from 'theme-ui'
import { Button, Svg } from 'ui'
import ButtonArrow from 'images/button-arrow-right.svg'
import { navigate } from 'gatsby'
import StarsWrapper, { bigStarStyles } from '../StarsWrapper'
import Logo from 'images/organization/homepage/sections/readyToGetStarted/controld-logo.svg'

export default function ReadyToGetStarted(): ReactElement {
  return (
    <Flex
      className="ios-scrolling-glitches-fix"
      data-testid="ready-to-get-started"
      sx={{
        contentVisibility: 'auto',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '2.1rem',
        position: 'relative',
        backgroundColor: 'blue800',
        py: ['6.4rem', '10rem'],
        borderColor: 'blueYonder20',
      }}
    >
      <Flex
        sx={{
          display: ['none', 'flex'],
          position: 'absolute',
          width: '100%',
          height: '80%',
          background:
            'linear-gradient(to bottom, rgba(246, 102, 255, 0.0) 0%, rgba(246, 102, 255, 0.01) 10%, rgba(246, 102, 255, 0.05) 30%, rgba(246, 102, 255, 0.05) 50%, rgba(246, 102, 255, 0.05) 70%, rgba(246, 102, 255, 0) 100%)',
        }}
      />
      <StarsWrapper
        starsSx={[
          {
            top: '9.3rem',
            left: '9.5rem',
            opacity: 0.25,
          },
          {
            top: '15rem',
            left: '-11.4rem',
          },
          {
            top: '30.9rem',
            left: '-4.1rem',
          },
          {
            top: '22.7rem',
            right: '-7.1rem',
            ...bigStarStyles,
          },
        ]}
      >
        <Flex
          sx={{
            backgroundColor: 'blue80',
            height: '48rem',
            width: '62.8rem',
            zIndex: 'pricingModal',
            mx: ['1.6rem', 0],
            border: '1px solid',
            borderColor: 'blueYonder20',
            borderRadius: '1rem',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <Flex
            sx={{
              width: ['auto', '48rem'],
              flexDirection: 'column',
              mt: ['6.4rem', 'auto'],
              mx: ['1.6rem', 0],
              gap: '2.4rem',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
            }}
          >
            <Flex
              sx={{
                justifyContent: 'center',
                alignItems: 'center',
                width: '7.2rem',
                height: '7.2rem',
                px: '1.5rem',
                py: '1.8rem',
                color: 'white',
                position: 'relative',
                boxSizing: 'border-box',
                background: 'linear-gradient(180deg, rgb(25,31,51, 1) 0%, rgb(25,31,51, 0.1) 100%)',
                backgroundClip: 'padding-box',
                borderRadius: '100%',
                /**
                 * Workaround for being able to give a border with border radius a gradient
                 * See: https://css-tricks.com/gradient-borders-in-css/
                 */
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 0,
                  zIndex: -1,
                  margin: '-1px',
                  borderRadius: 'inherit',
                  background:
                    'linear-gradient(180deg, rgba(198, 215, 255, 0.3) 0%, rgba(198, 215, 255, 0) 80%)',
                },
              }}
            >
              <Svg svg={Logo} descriptiveText="Control D company logo"></Svg>
            </Flex>
            <Text
              variant="size21Weight600Line138"
              as="h2"
              sx={{
                color: 'aliceBlue',
              }}
            >
              Ready to get started?
            </Text>
            <Flex
              sx={{
                gap: '1.6rem',
                alignContent: ['center', 'center', 'flex-start'],
                flexDirection: ['column', 'column', 'row'],
              }}
            >
              <Button
                data-testid="take-it-now-button"
                sx={{ whiteSpace: 'nowrap' }}
                variant="gradientButton"
                ariaLabel="Start Free Trial"
                name="Start Free Trial"
                onClick={() => navigate('/onboard')}
              >
                <Text>Start Free Trial</Text>
                <Svg
                  svg={ButtonArrow}
                  sx={{
                    ml: '0.6rem',
                  }}
                  descriptiveText="Right arrow icon for Start Free Trial button"
                />
              </Button>
              <Button
                ariaLabel="Book a Demo"
                name="Book a Demo"
                sx={{
                  whiteSpace: 'nowrap',
                  '@media screen and (min-width: 768px) and (max-width: 980px)': {
                    width: '100%',
                  },
                }}
                variant="roundedSecondary"
                onClick={() => navigate('/demo')}
              >
                <Text>Book a Demo</Text>
              </Button>
            </Flex>
          </Flex>

          <Flex
            sx={{
              flexDirection: ['column', 'row'],
              width: '100%',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderTop: '1px solid',
              borderColor: 'blueYonder20',
              mt: ['6.4rem', 'auto'],
              gap: ['1.4rem', 0],
              py: ['2.4rem', '2rem'],
              px: ['1.6rem', '2.4rem'],
            }}
          >
            <Text
              variant="size16Weight400Line140"
              sx={{
                textAlign: 'center',
                color: 'lightSteelBlue',
              }}
            >
              Need some help?
            </Text>

            <Button
              ariaLabel="Contact us"
              name="Contact us"
              variant="roundedTertiary"
              sx={{ width: ['100%', 'auto'] }}
              onClick={() => navigate('/contact')}
            >
              Contact us
            </Button>
          </Flex>
        </Flex>
      </StarsWrapper>
    </Flex>
  )
}
